import dayjs from 'dayjs'
import { useMemo } from 'react'

import { PlatePage_CulturePlate } from '~/api/backend/pages/platePageAPI'
import LineGraph from '~/components/LineGraph'
import { generateWellColor } from '~/pages/Monitor/utils/MonitorGraphColors'

export interface PlateAbsorbanceGraphSideBarProps {
  plate: PlatePage_CulturePlate
  highlightedWell?: string
}

interface AbsorbanceObservation {
  timestamp: number // Unix timestamp in seconds
  absorbance: number
}

export default function PlateAbsorbanceGraphSideBar({
  plate,
  highlightedWell,
}: PlateAbsorbanceGraphSideBarProps) {
  const { graphData, legendData, allTimestamps, minTime, maxTime } = useMemo(() => {
    const wellAbsorbanceData: { [well: string]: AbsorbanceObservation[] } = {}
    const wellColors: { [well: string]: string } = {}
    let wellIndex = 0

    // Extract absorbance data for each well from observation history
    plate.plateObservationHistory.forEach(observation => {
      if ((observation as any).absorbances) {
        const timestamp = dayjs(observation.timestamp).unix()
        ;(observation as any).absorbances.forEach((absorbance: any) => {
          const well = absorbance.well
          if (!wellAbsorbanceData[well]) {
            wellAbsorbanceData[well] = []
            wellColors[well] = generateWellColor(wellIndex)
            wellIndex++
          }
          wellAbsorbanceData[well].push({
            timestamp,
            absorbance: absorbance.value,
          })
        })
      }
    })

    // Sort data by timestamp for each well
    Object.keys(wellAbsorbanceData).forEach(well => {
      wellAbsorbanceData[well].sort((a, b) => a.timestamp - b.timestamp)
    })

    // Get all timestamps for domain calculation
    const allTimestamps = Object.values(wellAbsorbanceData)
      .flatMap(data => data.map(obs => obs.timestamp))
      .sort()
    const minTime = allTimestamps[0] ?? Date.now() / 1000
    const maxTime = allTimestamps[allTimestamps.length - 1] ?? Date.now() / 1000

    // Create legend data with latest values
    const legendData = Object.entries(wellAbsorbanceData).map(([well, data]) => ({
      well,
      color: wellColors[well],
      latestValue: data[data.length - 1]?.absorbance ?? 0,
    }))

    // Transform data to match D3LineGraphData format: { [key: string]: number }[]
    const transformedGraphData: { [well: string]: { [key: string]: number }[] } = {}
    Object.entries(wellAbsorbanceData).forEach(([well, observations]) => {
      transformedGraphData[well] = observations.map(obs => ({
        timestamp: obs.timestamp,
        absorbance: obs.absorbance,
      }))
    })

    return {
      graphData: transformedGraphData,
      legendData,
      allTimestamps,
      minTime,
      maxTime,
    }
  }, [plate.plateObservationHistory])

  // Return null if no absorbance data is available
  if (Object.keys(graphData).length === 0) {
    return null
  }

  // Calculate dynamic Y-axis domain based on absorbance values
  const allAbsorbanceValues = Object.values(graphData).flatMap(data =>
    data.map(obs => obs.absorbance),
  )
  const minAbsorbance = Math.min(...allAbsorbanceValues)
  const maxAbsorbance = Math.max(...allAbsorbanceValues)
  // Use a dynamic buffer so that small ranges are zoomed in but keep a minimum breathing room
  const range = maxAbsorbance - minAbsorbance
  const minBuffer = 0.002 // 2 milli-absorbance minimum padding
  const buffer = Math.max(range * 0.1, minBuffer)
  const yDomain: [number, number] = [
    Math.max(0, minAbsorbance - buffer),
    maxAbsorbance + buffer,
  ]

  // An empty region on either side of the X domain keeps the line from
  // overlapping the Y axis labels.
  const xAxisBuffer = ((maxTime - minTime) * 0.19) / 2
  const xDomain: [number, number] = [minTime - xAxisBuffer, maxTime + xAxisBuffer]
  const allDataOnSameDay =
    dayjs.unix(maxTime).format('YYMMDD') === dayjs.unix(minTime).format('YYMMDD')

  // Decide decimal precision dynamically based on the y-range so small ranges are readable
  const yRangeForFormat = yDomain[1] - yDomain[0]
  const _yDecimals = yRangeForFormat < 0.01 ? 4 : yRangeForFormat < 0.1 ? 3 : 2

  return (
    <aside className='border-t-0.5 border-grey-light py-4 w-full h-full'>
      <h2 className='text-sm font-medium text-grey-darkest mb-4.5'>
        Absorbance Over Time
      </h2>

      <LineGraph
        className='h-[600px] w-full'
        layoutOptions={{
          marginTop: 0,
          marginRight: 0,
          marginLeft: 50,
        }}
        axisOptions={{
          xAxisBorderClassName: 'stroke-grey-medium',
          xTickValues:
            allTimestamps.length === 1
              ? [minTime]
              : [minTime, minTime + (maxTime - minTime) / 2, maxTime],
          xTickFormat:
            allDataOnSameDay && allTimestamps.length > 1
              ? (unix: number) => dayjs.unix(unix).format('h:mm A')
              : (unix: number) => dayjs.unix(unix).format('MMM D'),
          xTickSize: 0,
          xTickLabelClassName: 'text-xs font-bold translate-y-1',

          yAxisBorderClassName: 'hidden',
          yTickValues: (() => {
            const range = maxAbsorbance - minAbsorbance
            const step = range / 4
            return [
              Math.round((minAbsorbance + step) * 100) / 100,
              Math.round((minAbsorbance + 2 * step) * 100) / 100,
              Math.round((minAbsorbance + 3 * step) * 100) / 100,
              Math.round(maxAbsorbance * 100) / 100,
            ]
          })(),
          yTickFormat: (value: number) => value.toPrecision(2),
          yTickLineClassName: 'stroke-grey-lightest stroke-1 w-full',
          yTickLabelClassName:
            'text-xs font-bold text-start translate-x-0.5 translate-y-2.25 w-full',
          yTickSize: -5000,
        }}
        options={{
          dotSize: 0,
          xDomain,
          xKey: 'timestamp',
          yDomain,
          yKey: 'absorbance',
          lineColor: (key: string) => {
            const well = key
            const wellData = legendData.find(item => item.well === well)
            return wellData?.color ?? '#ccc'
          },
          highlightedLine: highlightedWell,
        }}
        data={graphData}
      />
      {highlightedWell && (
        <div className='text-grey-dark font-subtext text-center font-bold'>
          Well {highlightedWell}
        </div>
      )}
    </aside>
  )
}
